/**
 * Centralized Access Control Module for Multi-Clinic Patient Data Access
 * 
 * This module manages access permissions across clinics following these rules:
 * 1. Patient data is only accessible to clinics where the patient is added
 * 2. Conditions are only visible to the clinic that created them
 * 3. External providers (referees) can only see patients/conditions where they're assigned
 * 4. Documents are filtered based on accessible conditions
 * 5. Care team members are filtered by clinic
 * 6. Health notes follow same rules as conditions
 */

import { firestoreOperationsDb } from '../firebase/firebase';
import appConstants from '../appConstants';

/**
 * Super Admin detection (admin portal route: /admin)
 *
 * Admin portal login stores a lightweight session in localStorage under key "user":
 * { user_id, name, email, isAdmin: true, role: 'admin' }
 *
 * Super Admin must be able to access ALL records (all clinics).
 */
export function isSuperAdminSession() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.isAdmin === true && parsed?.role === 'admin';
  } catch (e) {
    return false;
  }
}

/**
 * Check if a user has access to a specific patient
 * @param {string} userId - The user's ID
 * @param {string} patientId - The patient's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role (ADMIN, TEAMMEMBER, etc.)
 * @returns {Promise<boolean>} - Whether the user has access
 */
export async function hasPatientAccess(userId, patientId, userClinicId, userRole) {
  try {
    // Super admin (admin portal) can access all patients across clinics
    if (isSuperAdminSession()) {
      console.log('[Access Control] Super admin access granted');
      return true;
    }

    // If missing required parameters, allow access (backward compatibility)
    if (!userId || !patientId) {
      console.warn('[Access Control] Missing userId or patientId - allowing access for backward compatibility');
      return true;
    }

    // Admin and team members: Check if their clinic is in patient's clinicId array OR task assignments
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      if (!userClinicId) {
        console.warn('[Access Control] Missing userClinicId - allowing access for backward compatibility');
        return true;
      }

      const patientDoc = await firestoreOperationsDb
        .collection('patients')
        .doc(patientId)
        .get();
      
      if (!patientDoc.exists) {
        console.warn(`[Access Control] Patient ${patientId} not found`);
        return false;
      }
      
      const patientData = patientDoc.data();
      const patientClinics = patientData.clinicId || [];
      
      // Handle both array and single value (backward compatibility)
      const patientClinicsArray = Array.isArray(patientClinics) ? patientClinics : [patientClinics];
      
      // Check if patient is directly in clinic
      if (patientClinicsArray.includes(userClinicId)) {
        console.log('[Access Control] ✅ Patient is directly in clinic', {
          userId,
          userClinicId,
          patientId
        });
        return true;
      }
      
      // If not directly in clinic, check if any clinic member is assigned to referral/takeTest tasks
      console.log('[Access Control] Patient not directly in clinic, checking task assignments...');
      
      // Get all clinic members' emails
      const clinicMembersSnapshot = await firestoreOperationsDb
        .collection('users')
        .where('clinicId', '==', userClinicId)
        .where('isDeleted', '==', false)
        .get();
      
      const clinicMemberEmails = new Set();
      clinicMembersSnapshot.forEach(doc => {
        if (doc.data().email) clinicMemberEmails.add(doc.data().email.toLowerCase());
      });
      
      console.log(`[Access Control] Checking ${clinicMemberEmails.size} clinic member emails for task assignments`);
      
      // Check referral and takeTest tasks
      const referralTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'referral')
        .where('isDeleted', '==', false)
        .get();

      const takeTestTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'takeTest')
        .where('isDeleted', '==', false)
        .get();

      const allRelevantTasks = [...referralTasksQuery.docs, ...takeTestTasksQuery.docs];
      console.log(`[Access Control] Found ${allRelevantTasks.length} referral/takeTest tasks for patient ${patientId}`);
      
      // Check if any task has a clinic member's email
      for (const taskDoc of allRelevantTasks) {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        const emailsToCheck = [];

        // Root level
        if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
        if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
        
        // Nested in task type
        if (taskData[taskType]) {
          if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
          if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
        }

        console.log(`[Access Control] Task ${taskDoc.id} emails:`, emailsToCheck);

        // Check if any email matches clinic members
        for (const email of emailsToCheck) {
          if (email && clinicMemberEmails.has(email.toLowerCase())) {
            console.log(`[Access Control] ✅ Clinic member ${email} assigned to task ${taskDoc.id}`);
            return true;
          }
        }
      }
      
      console.log('[Access Control] ❌ No clinic access or task assignments found');
      return false;
    }
    
    // Providers/External Referees: Check if they're in care team OR assigned via email
    console.log(`[Access Control] Checking external provider access for userId: ${userId}`);
    
    // Method 1: Check care team
    const careTeamQuery = await firestoreOperationsDb
      .collection('careTeam')
      .where('userId', '==', userId)
      .where('patientId', '==', patientId)
      .where('isDeleted', '==', false)
      .where('status', '!=', 'deactivated')
      .get();
    
    if (!careTeamQuery.empty) {
      console.log(`[Access Control] ✅ User ${userId} found in care team`);
      return true;
    }
    
    console.log(`[Access Control] User not in care team, checking email assignments...`);
    
    // Method 2: Check if user's email is in referral/takeTest tasks
    const userDoc = await firestoreOperationsDb.collection('users').doc(userId).get();
    const userEmail = userDoc.exists ? userDoc.data().email : null;

    if (userEmail) {
      console.log(`[Access Control] User email: ${userEmail}`);
      
      const referralTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'referral')
        .where('isDeleted', '==', false)
        .get();

      const takeTestTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'takeTest')
        .where('isDeleted', '==', false)
        .get();

      const allRelevantTasks = [...referralTasksQuery.docs, ...takeTestTasksQuery.docs];
      console.log(`[Access Control] Found ${allRelevantTasks.length} referral/takeTest tasks`);

      for (const taskDoc of allRelevantTasks) {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        const emailsToCheck = [];

        // Root level
        if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
        if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
        
        // Nested in task type
        if (taskData[taskType]) {
          if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
          if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
        }

        console.log(`[Access Control] Checking task ${taskDoc.id} emails:`, emailsToCheck);

        for (const email of emailsToCheck) {
          if (email && email.toLowerCase() === userEmail.toLowerCase()) {
            console.log(`[Access Control] ✅ Email match! User ${userId} (${userEmail}) can access patient ${patientId}`);
            return true;
          }
        }
      }
    } else {
      console.warn(`[Access Control] User email not found for userId: ${userId}`);
    }
    
    console.log(`[Access Control] ❌ No access found for user ${userId} to patient ${patientId}`);
    return false;
  } catch (error) {
    console.error('[Access Control] Error checking patient access:', error);
    // Return true on error for backward compatibility (don't break existing functionality)
    return true;
  }
}

/**
 * Get accessible conditions for a user
 * @param {string} userId - The user's ID
 * @param {string} patientId - The patient's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @param {Array} allConditions - All conditions for the patient
 * @returns {Promise<Array>} - Filtered conditions the user can access
 */
export async function getAccessibleConditions(userId, patientId, userClinicId, userRole, allConditions) {
  try {
    // Super admin (admin portal) can access all conditions across clinics
    if (isSuperAdminSession()) return allConditions || [];

    // Admin and team members: See conditions where their clinic members are in care team
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      // Get all users from this clinic
      const clinicMembersQuery = await firestoreOperationsDb
        .collection('users')
        .where('clinicId', '==', userClinicId)
        .where('isDeleted', '==', false)
        .get();
      
      const clinicMemberIds = new Set();
      const clinicMemberEmails = new Set();
      clinicMembersQuery.forEach(doc => {
        clinicMemberIds.add(doc.id);
        const email = doc.data().email;
        if (email) clinicMemberEmails.add(email.toLowerCase());
      });
      
      console.log(`[Access Control] Found ${clinicMemberIds.size} members in clinic ${userClinicId}`);
      
      // Get all care team records for this patient
      const careTeamQuery = await firestoreOperationsDb
        .collection('careTeam')
        .where('patientId', '==', patientId)
        .where('isDeleted', '==', false)
        .get();
      
      // Build set of condition IDs where clinic members are in care team
      const accessibleConditionIds = new Set();
      
      careTeamQuery.forEach(doc => {
        const careTeamData = doc.data();
        const careTeamUserId = careTeamData.userId;
        
        // Check if this care team member is from the user's clinic
        if (clinicMemberIds.has(careTeamUserId)) {
          // Add all conditions this care team member has access to
          if (careTeamData.conditions && Array.isArray(careTeamData.conditions)) {
            careTeamData.conditions.forEach(cond => {
              if (cond.conditionId && cond.isVisible !== false) {
                accessibleConditionIds.add(cond.conditionId);
              }
            });
          }
        }
      });
      
      // Also check referral/takeTest tasks for email-based assignments
      const referralTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'referral')
        .where('isDeleted', '==', false)
        .get();
      
      const takeTestTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'takeTest')
        .where('isDeleted', '==', false)
        .get();
      
      const allRelevantTasks = [...referralTasksQuery.docs, ...takeTestTasksQuery.docs];
      
      allRelevantTasks.forEach(taskDoc => {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        const emailsToCheck = [];
        
        // Check root level emails
        if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
        if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
        
        // Check nested emails
        if (taskData[taskType]) {
          if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
          if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
        }
        
        // Check if any email matches clinic members
        for (const email of emailsToCheck) {
          if (email && clinicMemberEmails.has(email.toLowerCase())) {
            if (taskData.conditionId) {
              accessibleConditionIds.add(taskData.conditionId);
              console.log(`[Access Control] Clinic member email ${email} found in ${taskType} task ${taskDoc.id}, adding condition ${taskData.conditionId}`);
              break; // Found a match, no need to check other emails
            }
          }
        }
      });
      
      console.log(`[Access Control] Clinic ${userClinicId} has access to ${accessibleConditionIds.size} conditions`);
      
      // Filter conditions to only include accessible ones
      return allConditions.filter(condition => 
        accessibleConditionIds.has(condition.conditionId)
      );
    }
    
    // External providers/referees: See only conditions where they're assigned
    // First, get user's email for matching against external clinician emails
    const userDoc = await firestoreOperationsDb
      .collection('users')
      .doc(userId)
      .get();
    
    const userEmail = userDoc.exists ? userDoc.data().email : null;
    
    // Method 1: Check care team records (for providers added via care team)
    const careTeamQuery = await firestoreOperationsDb
      .collection('careTeam')
      .where('userId', '==', userId)
      .where('patientId', '==', patientId)
      .where('isDeleted', '==', false)
      .get();
    
    // Extract condition IDs from care team records
    const accessibleConditionIds = new Set();
    careTeamQuery.forEach(doc => {
      const careTeamData = doc.data();
      if (careTeamData.conditions && Array.isArray(careTeamData.conditions)) {
        careTeamData.conditions.forEach(cond => {
          if (cond.conditionId && cond.isVisible !== false) {
            accessibleConditionIds.add(cond.conditionId);
          }
        });
      }
    });
    
    // Method 2: Check tasks for external clinician email matches
    if (userEmail) {
      console.log(`[Access Control] Checking task assignments for external provider ${userEmail}`);
      
      // Get referral and takeTest tasks for this patient
      const referralTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'referral')
        .where('isDeleted', '==', false)
        .get();
      
      const takeTestTasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('patientId', '==', patientId)
        .where('type', '==', 'takeTest')
        .where('isDeleted', '==', false)
        .get();
      
      const allRelevantTasks = [...referralTasksQuery.docs, ...takeTestTasksQuery.docs];
      
      allRelevantTasks.forEach(taskDoc => {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        const emailsToCheck = [];
        
        // Check root level emails
        if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
        if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
        
        // Check nested emails in task type
        if (taskData[taskType]) {
          if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
          if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
        }
        
        // Check if any email matches user's email
        for (const email of emailsToCheck) {
          if (email && email.toLowerCase() === userEmail.toLowerCase()) {
            if (taskData.conditionId) {
              accessibleConditionIds.add(taskData.conditionId);
              console.log(`[Access Control] External provider ${userEmail} has access to condition ${taskData.conditionId} via ${taskType} task ${taskDoc.id}`);
              break; // Found a match, no need to check other emails
            }
          }
        }
      });
    }
    
    console.log(`[Access Control] External provider ${userId} (${userEmail}) has access to ${accessibleConditionIds.size} conditions`);
    
    // Filter conditions to only include accessible ones
    return allConditions.filter(condition => 
      accessibleConditionIds.has(condition.conditionId)
    );
  } catch (error) {
    console.error('Error getting accessible conditions:', error);
    return [];
  }
}

/**
 * Check if a user has access to a specific condition
 * @param {string} userId - The user's ID
 * @param {string} conditionId - The condition's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @returns {Promise<boolean>} - Whether the user has access
 */
export async function hasConditionAccess(userId, conditionId, userClinicId, userRole) {
  try {
    // Super admin (admin portal) can access all conditions across clinics
    if (isSuperAdminSession()) return true;

    // Get the condition
    const conditionDoc = await firestoreOperationsDb
      .collection('conditions')
      .doc(conditionId)
      .get();
    
    if (!conditionDoc.exists) return false;
    
    const conditionData = conditionDoc.data();
    
    // Admin and team members: Check if any clinic member is in care team for this condition
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      // Get all users from this clinic
      const clinicMembersQuery = await firestoreOperationsDb
        .collection('users')
        .where('clinicId', '==', userClinicId)
        .where('isDeleted', '==', false)
        .get();
      
      const clinicMemberIds = new Set();
      const clinicMemberEmails = new Set();
      clinicMembersQuery.forEach(doc => {
        clinicMemberIds.add(doc.id);
        const email = doc.data().email;
        if (email) clinicMemberEmails.add(email.toLowerCase());
      });
      
      // Check care team for this patient to see if any clinic member has this condition
      const careTeamQuery = await firestoreOperationsDb
        .collection('careTeam')
        .where('patientId', '==', conditionData.patientId)
        .where('isDeleted', '==', false)
        .get();
      
      for (const doc of careTeamQuery.docs) {
        const careTeamData = doc.data();
        const careTeamUserId = careTeamData.userId;
        
        // Check if this care team member is from the user's clinic
        if (clinicMemberIds.has(careTeamUserId)) {
          // Check if they have access to this specific condition
          if (careTeamData.conditions && Array.isArray(careTeamData.conditions)) {
            const hasCondition = careTeamData.conditions.some(
              cond => cond.conditionId === conditionId && cond.isVisible !== false
            );
            if (hasCondition) {
              console.log(`[Access Control] Clinic ${userClinicId} member ${careTeamUserId} has access to condition ${conditionId} via care team`);
              return true;
            }
          }
        }
      }
      
      // Also check tasks for email-based assignments
      const tasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('conditionId', '==', conditionId)
        .where('isDeleted', '==', false)
        .get();
      
      for (const taskDoc of tasksQuery.docs) {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        
        if (taskData[taskType]) {
          const clinicianEmail = taskData[taskType].clinicianEmail || taskData[taskType].recipientEmail;
          if (clinicianEmail && clinicMemberEmails.has(clinicianEmail.toLowerCase())) {
            console.log(`[Access Control] Clinic ${userClinicId} member has access to condition ${conditionId} via task email assignment`);
            return true;
          }
        }
      }
      
      return false;
    }
    
    // External providers/referees: Check if they're in care team for this condition
    // Get user's email for matching
    const userDoc = await firestoreOperationsDb
      .collection('users')
      .doc(userId)
      .get();
    
    const userEmail = userDoc.exists ? userDoc.data().email : null;
    
    // Method 1: Check care team records
    const careTeamQuery = await firestoreOperationsDb
      .collection('careTeam')
      .where('userId', '==', userId)
      .where('patientId', '==', conditionData.patientId)
      .where('isDeleted', '==', false)
      .get();
    
    // Check if any care team record includes this condition
    let hasAccess = false;
    careTeamQuery.forEach(doc => {
      const careTeamData = doc.data();
      if (careTeamData.conditions && Array.isArray(careTeamData.conditions)) {
        const hasCondition = careTeamData.conditions.some(
          cond => cond.conditionId === conditionId && cond.isVisible !== false
        );
        if (hasCondition) hasAccess = true;
      }
    });
    
    if (hasAccess) return true;
    
    // Method 2: Check if user's email matches any external clinician in tasks under this condition
    if (userEmail) {
      const tasksQuery = await firestoreOperationsDb
        .collection('tasks')
        .where('conditionId', '==', conditionId)
        .where('isDeleted', '==', false)
        .get();
      
      for (const taskDoc of tasksQuery.docs) {
        const taskData = taskDoc.data();
        const taskType = taskData.type;
        
        // Check clinicianEmail field
        if (taskData[taskType] && taskData[taskType].clinicianEmail) {
          if (taskData[taskType].clinicianEmail.toLowerCase() === userEmail.toLowerCase()) {
            console.log(`[Access Control] External provider ${userEmail} has access to condition ${conditionId} via task email match`);
            return true;
          }
        }
        
        // Check recipientEmail field
        if (taskData[taskType] && taskData[taskType].recipientEmail) {
          if (taskData[taskType].recipientEmail.toLowerCase() === userEmail.toLowerCase()) {
            console.log(`[Access Control] External provider ${userEmail} has access to condition ${conditionId} via task recipient email match`);
            return true;
          }
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking condition access:', error);
    return false;
  }
}

/**
 * Filter documents based on accessible conditions and task assignments
 * @param {string} userId - The user's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @param {Array} allDocuments - All documents for the patient
 * @param {Array} accessibleConditionIds - Array of condition IDs the user can access
 * @param {string} userEmail - The user's email (optional)
 * @returns {Array} - Filtered documents
 */
export async function filterAccessibleDocuments(userId, userClinicId, userRole, allDocuments, accessibleConditionIds, userEmail = null) {
  try {
    
    // Super admin (admin portal) can access all documents across clinics
    if (isSuperAdminSession()) return allDocuments || [];

    // Get user email if not provided
    if (!userEmail && userId) {
      const userDoc = await firestoreOperationsDb.collection('users').doc(userId).get();
      userEmail = userDoc.exists ? userDoc.data().email : null;
    }

    // Admin and team members: See documents for conditions created by their clinic OR forms assigned to them OR documents they uploaded
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      // Get user's email if not provided
      if (!userEmail && userId) {
        const userDoc = await firestoreOperationsDb.collection('users').doc(userId).get();
        userEmail = userDoc.exists ? userDoc.data().email : null;
      }

      return allDocuments.filter(doc => {
        
        
        // Check 1: If document was uploaded by this user, they can see it
        if (doc.uploadedBy) {
          // uploadedBy can be an object with userId or just a userId string
          const uploadedByUserId = typeof doc.uploadedBy === 'object' ? doc.uploadedBy.userId : doc.uploadedBy;
          if (uploadedByUserId === userId) {
            console.log(`[Access Control] User ${userId} can access document ${doc.documentId} - they uploaded it`);
            return true;
          }
        }
        
        // Check 2: If it's a form document (task type: referral or takeTest)
        // Clinic members might be assigned as external referees
        if (doc.type === 'task' && doc.data && (doc.data.referral || doc.data.takeTest)) {
          const taskData = doc.data;
          const taskType = taskData.type; // 'referral' or 'takeTest'
          
          if (taskData[taskType] && userEmail) {
            const emailsToCheck = [];
            
            // Check recipientEmail and clinicianEmail in the task
            if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
            if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
            
            // Also check root level emails
            if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
            if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
            
            // If user's email matches, they can see this form (as a referee)
            for (const email of emailsToCheck) {
              if (email && email.toLowerCase() === userEmail.toLowerCase()) {
                console.log(`[Access Control] Clinic user ${userEmail} can access form document as referee for task ${doc.documentId}`);
                return true;
              }
            }
          }
        }
        
        // Check 3: If document has no condition, check if uploaded by clinic member
        if (!doc?.conditionId?.conditionId) {
          // Check if uploadedBy is from the same clinic
          return true; // Will be filtered separately
        }
        
        // Check 4: If document is linked to conditions, check if any are accessible
        if (Array.isArray(doc?.conditionId?.conditionId)) {
          return doc?.conditionId?.conditionId.some(condId => accessibleConditionIds.includes(condId));
        }
        
        return accessibleConditionIds.includes(doc?.conditionId?.conditionId);
      });
    }
    
    // External providers/referees: See documents for their assigned conditions OR forms assigned to them OR documents they uploaded
    return allDocuments.filter(doc => {
      
      
      // Check 1: If document was uploaded by this user, they can see it
      if (doc.uploadedBy) {
        // uploadedBy can be an object with userId or just a userId string
        const uploadedByUserId = typeof doc.uploadedBy === 'object' ? doc.uploadedBy.userId : doc.uploadedBy;
        if (uploadedByUserId === userId) {
          console.log(`[Access Control] User ${userId} can access document ${doc.documentId} - they uploaded it`);
          return true;
        }
      }
      
      // Check 2: If it's a form document (task type: referral or takeTest) assigned to them
      if (doc.type === 'task' && doc.data && (doc.data.referral || doc.data.takeTest)) {
        const taskData = doc.data;
        const taskType = taskData.type; // 'referral' or 'takeTest'
        
        if (taskData[taskType] && userEmail) {
          const emailsToCheck = [];
          
          // Check recipientEmail and clinicianEmail in the task
          if (taskData[taskType].recipientEmail) emailsToCheck.push(taskData[taskType].recipientEmail);
          if (taskData[taskType].clinicianEmail) emailsToCheck.push(taskData[taskType].clinicianEmail);
          
          // Also check root level emails
          if (taskData.recipientEmail) emailsToCheck.push(taskData.recipientEmail);
          if (taskData.clinicianEmail) emailsToCheck.push(taskData.clinicianEmail);
          
          // If user's email matches, they can see this form
          for (const email of emailsToCheck) {
            if (email && email.toLowerCase() === userEmail.toLowerCase()) {
              console.log(`[Access Control] User ${userEmail} can access form document for task ${doc.documentId}`);
              return true;
            }
          }
        }
      }
      
      // Check 3: For regular documents, check condition access
      if (!doc.conditionId?.conditionId) {
        return false; // Can't see uncategorized documents (unless they uploaded it - checked above)
      }
      
      if (Array.isArray(doc?.conditionId?.conditionId)) {
        return doc?.conditionId?.conditionId.some(condId => accessibleConditionIds.includes(condId));
      }
      
      return accessibleConditionIds.includes(doc?.conditionId?.conditionId);
    });
  } catch (error) {
    console.error('Error filtering accessible documents:', error);
    return [];
  }
}

/**
 * Filter care team members based on clinic
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @param {Array} allCareTeam - All care team members
 * @param {Array} accessibleConditionIds - Array of condition IDs the user can access
 * @returns {Array} - Filtered care team members
 */
export function filterAccessibleCareTeam(userClinicId, userRole, allCareTeam, accessibleConditionIds) {
  try {
    // Super admin (admin portal) can access all care team across clinics
    if (isSuperAdminSession()) return allCareTeam || [];

    // Admin and team members: See care team for their clinic's conditions
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      return allCareTeam.filter(member => {
        // Check if this member's conditions overlap with accessible conditions
        if (member.conditions && Array.isArray(member.conditions)) {
          return member.conditions.some(cond => 
            accessibleConditionIds.includes(cond.conditionId)
          );
        }
        
        // If member has conditionId field (flattened structure)
        if (member.conditionId) {
          return accessibleConditionIds.includes(member.conditionId);
        }
        
        return false;
      });
    }
    
    // External providers: See only care team for their assigned conditions
    return allCareTeam.filter(member => {
      if (member.conditions && Array.isArray(member.conditions)) {
        return member.conditions.some(cond => 
          accessibleConditionIds.includes(cond.conditionId)
        );
      }
      
      if (member.conditionId) {
        return accessibleConditionIds.includes(member.conditionId);
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error filtering accessible care team:', error);
    return [];
  }
}

/**
 * Filter health notes based on accessible conditions
 * @param {Array} allHealthNotes - All health notes (conditions with nested notes)
 * @param {Array} accessibleConditionIds - Array of condition IDs the user can access
 * @returns {Array} - Filtered health notes
 */
export function filterAccessibleHealthNotes(allHealthNotes, accessibleConditionIds) {
  try {
    // Super admin (admin portal) can access all health notes across clinics
    if (isSuperAdminSession()) return allHealthNotes || [];

    // Filter to only include conditions that are accessible
    return allHealthNotes.filter(condition => 
      accessibleConditionIds.includes(condition.conditionId)
    );
  } catch (error) {
    console.error('Error filtering accessible health notes:', error);
    return [];
  }
}

/**
 * Get user's clinic and role information
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - User's clinic and role information
 */
export async function getUserAccessInfo(userId) {
  try {
    const userDoc = await firestoreOperationsDb
      .collection('users')
      .doc(userId)
      .get();
    
    if (!userDoc.exists) {
      return { clinicId: null, role: null, exists: false };
    }
    
    const userData = userDoc.data();
    return {
      clinicId: userData.clinicId || null,
      role: userData.role || null,
      exists: true,
      userData
    };
  } catch (error) {
    console.error('Error getting user access info:', error);
    return { clinicId: null, role: null, exists: false };
  }
}

/**
 * Check if user can upload documents to a specific condition
 * @param {string} userId - The user's ID
 * @param {string} conditionId - The condition's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @returns {Promise<boolean>} - Whether the user can upload
 */
export async function canUploadToCondition(userId, conditionId, userClinicId, userRole) {
  // Use the same logic as condition access
  return await hasConditionAccess(userId, conditionId, userClinicId, userRole);
}

/**
 * Get accessible condition IDs for a user and patient
 * Helper function that returns just the IDs
 * @param {string} userId - The user's ID
 * @param {string} patientId - The patient's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @returns {Promise<Array<string>>} - Array of accessible condition IDs
 */
export async function getAccessibleConditionIds(userId, patientId, userClinicId, userRole) {
  try {
    // Super admin (admin portal) can access all conditions across clinics
    if (isSuperAdminSession()) {
      const conditionsQuery = await firestoreOperationsDb
        .collection('conditions')
        .where('patientId', '==', patientId)
        .where('isDeleted', '==', false)
        .get();
      return conditionsQuery.docs.map((d) => d.id);
    }

    // Admin and team members: Get conditions created by their clinic
    if (userRole === appConstants.roles.ADMIN || userRole === appConstants.roles.TEAMMEMBER) {
      const conditionsQuery = await firestoreOperationsDb
        .collection('conditions')
        .where('patientId', '==', patientId)
        .where('isDeleted', '==', false)
        .get();
      
      const accessibleIds = [];
      
      for (const doc of conditionsQuery.docs) {
        const conditionData = doc.data();
        
        // Check if condition has clinicId field
        if (conditionData.clinicId) {
          if (conditionData.clinicId === userClinicId) {
            accessibleIds.push(doc.id);
          }
        } else {
          // Backward compatibility: Check creator's clinic
          if (conditionData.createdBy) {
            const creatorDoc = await firestoreOperationsDb
              .collection('users')
              .doc(conditionData.createdBy)
              .get();
            
            if (creatorDoc.exists) {
              const creatorData = creatorDoc.data();
              if (creatorData.clinicId === userClinicId) {
                accessibleIds.push(doc.id);
              }
            }
          }
        }
      }
      
      return accessibleIds;
    }
    
    // External providers/referees: Get conditions from care team
    const careTeamQuery = await firestoreOperationsDb
      .collection('careTeam')
      .where('userId', '==', userId)
      .where('patientId', '==', patientId)
      .where('isDeleted', '==', false)
      .get();
    
    if (careTeamQuery.empty) return [];
    
    const accessibleConditionIds = new Set();
    careTeamQuery.forEach(doc => {
      const careTeamData = doc.data();
      if (careTeamData.conditions && Array.isArray(careTeamData.conditions)) {
        careTeamData.conditions.forEach(cond => {
          if (cond.conditionId && cond.isVisible !== false) {
            accessibleConditionIds.add(cond.conditionId);
          }
        });
      }
    });
    
    return Array.from(accessibleConditionIds);
  } catch (error) {
    console.error('Error getting accessible condition IDs:', error);
    return [];
  }
}

/**
 * Validate patient access and redirect if unauthorized
 * Use this in componentDidMount/componentWillMount to protect routes
 * @param {string} patientId - The patient's ID from URL
 * @param {string} userId - The user's ID
 * @param {string} userClinicId - The user's clinic ID
 * @param {number} userRole - The user's role
 * @param {Function} redirectCallback - Callback to redirect (e.g., history.push)
 * @returns {Promise<boolean>} - Whether user has access
 */
export async function validatePatientAccessOrRedirect(patientId, userId, userClinicId, userRole, redirectCallback) {
  try {
    // Super admin has access to everything
    if (isSuperAdminSession()) {
      console.log('[Route Protection] Super admin - access granted');
      return true;
    }
    
    // If missing critical parameters, allow access (backward compatibility)
    if (!userId || !patientId) {
      console.warn('[Route Protection] Missing userId or patientId - allowing access for backward compatibility');
      return true;
    }
    
    const hasAccess = await hasPatientAccess(userId, patientId, userClinicId, userRole);
    
    if (!hasAccess) {
      console.warn(`[Route Protection] Access denied: User ${userId} (clinic: ${userClinicId}) cannot access patient ${patientId}`);
      if (redirectCallback && typeof redirectCallback === 'function') {
        redirectCallback('/dashboard');
      }
      return false;
    }
    
    console.log(`[Route Protection] Access granted: User ${userId} can access patient ${patientId}`);
    return true;
  } catch (error) {
    console.error('[Route Protection] Error validating patient access:', error);
    // On error, allow access for backward compatibility (don't break existing functionality)
    console.warn('[Route Protection] Allowing access due to error (backward compatibility)');
    return true;
  }
}

export default {
  hasPatientAccess,
  getAccessibleConditions,
  hasConditionAccess,
  filterAccessibleDocuments,
  filterAccessibleCareTeam,
  filterAccessibleHealthNotes,
  getUserAccessInfo,
  canUploadToCondition,
  getAccessibleConditionIds,
  validatePatientAccessOrRedirect
};

